import { 
    Document, 
    Paragraph, 
    Table, 
    TableRow, 
    TableCell, 
    TextRun, 
    HeadingLevel, 
    AlignmentType,
    WidthType,
    VerticalAlign,
    ImageRun,
    Packer,
    TableOfContents,
    Footer,
    Header,
    PageNumber,
    BorderStyle,
    LineRuleType,
    ShadingType,
} from 'docx';
import { saveAs } from 'file-saver';
import { 
    formatScore, 
    formatValue, 
    formatDate, 
    formatUserList, 
    generateFilename,
    getStatusLabel,
    base64ToBuffer,
    svgToBase64 
} from './ExportUtils';
import { importanceLevels } from '../../../context/global';
import logoSvg from '../../../assets/hwbp-logo.svg';

/**
 * Generate and download a DOCX export of the project
 * @param {Object} project - Full project object from Redux
 * @param {Object} selections - User selections for export (includeExplanationTextBoxes adds bordered note areas)
 * @param {Object} flatKpis - Flat KPI lookup object
 * @param {Object} flatThemes - Flat theme lookup object
 * @param {Array} chartImages - Array of chart images as base64
 */
export const generateDocxExport = async (project, selections, flatKpis, flatThemes, chartImages = []) => {
    try {
        // Convert logo to base64
        // eslint-disable-next-line no-unused-vars
        const logoBase64 = await svgToBase64(logoSvg);
        
        const sections = [];
        
        // Title page
        sections.push(...createTitleSection(project));

        // Table of contents
        sections.push(...createTableOfContentsSection());
        
        // Project summary (always included)
        sections.push(...createProjectSummary(project, !!selections.includeExplanationTextBoxes));
        
        // Process each selected loop
        for (const loopSelection of selections.loops || []) {
            const loop = findLoop(project, loopSelection.loopId);
            if (!loop) continue;
            
            const phase = findPhaseForLoop(project, loopSelection.loopId);
            
            // Loop header
            sections.push(...createLoopHeader(phase, loop));
            
            // Loop setup/configuration
            if (loopSelection.includeSetup) {
                sections.push(...createLoopSetup(
                    loop,
                    flatKpis,
                    flatThemes,
                    loopSelection,
                    chartImages,
                    !!selections.includeExplanationTextBoxes
                ));
            }

            // Loop advice
            if (loopSelection.includeAdvice) {
                sections.push(...createLoopAdvice(loop, flatThemes));
            }
            
            // Scores table
            if (loopSelection.includeScoresTable) {
                sections.push(...createScoresTable(loop, loopSelection, flatKpis));
            }

            // Graphs (includes theme polar chart if selected)
            if (loopSelection.graphs && loopSelection.graphs.length > 0) {
                sections.push(...createGraphsSection(
                    loopSelection,
                    chartImages,
                    flatKpis,
                    !!selections.includeExplanationTextBoxes
                ));
            }

            // Conclusions
            if (loopSelection.includeConclusions) {
                sections.push(...createConclusionsSection(loop));
            }
        }
        
        // Create document
        const doc = new Document({
          styles: {
            paragraphStyles: [
              {
                // Only `name` prop required, `id` not necessary
                name: "Normal",
                run: {
                    font: "Roboto"
                },
              },
            ],
          },
          sections: [
            {
              properties: {
                page: {
                  margin: {
                    top: 360, // 1 inch = 1440 twips
                    right: 1440,
                    bottom: 1440,
                    left: 1440,
                  },
                },
              },
              headers: {
                default: new Header({
                  children: [
                    new Table({
                      rows: [
                        new TableRow({
                          height: {
                            value: 1000,
                            rule: "exact",
                          },
                          children: [
                            new TableCell({
                              children: [
                                new Paragraph({
                                  children: [
                                    new ImageRun({
                                      type: "jpg",
                                      data: base64ToBuffer(logoBase64),
                                      transformation: {
                                        width: 100,
                                        height: 55,
                                      },
                                    }),
                                  ],
                                  alignment: AlignmentType.LEFT,
                                }),
                              ],
                              verticalAlign: VerticalAlign.CENTER,
                            }),
                          ],
                        }),
                      ],
                      width: {
                        size: 100,
                        type: WidthType.PERCENTAGE,
                      },
                      borders: {
                        top: { style: BorderStyle.NONE },
                        bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE },
                        right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE },
                        insideVertical: { style: BorderStyle.NONE },
                      },
                    }),
                  ],
                }),
              },
              footers: {
                default: new Footer({
                  children: [
                    new Paragraph({
                      alignment: AlignmentType.CENTER,
                      children: [
                        new TextRun({
                          children: ["Pagina: ", PageNumber.CURRENT],
                        }),
                        new TextRun({
                          children: [" van ", PageNumber.TOTAL_PAGES],
                        }),
                      ],
                    }),
                  ],
                }),
              },
              children: sections,
            },
          ],
        });
        
        // Generate and download
        const blob = await Packer.toBlob(doc);
        saveAs(blob, generateFilename(project));
        
        return true;
    } catch (error) {
        console.error('Error generating DOCX:', error);
        throw error;
    }
};

// Helper function to find loop by ID
const findLoop = (project, loopId) => {
    for (const phase of project.phases || []) {
        const loop = phase.loops?.find(l => l.id === loopId);
        if (loop) return loop;
    }
    return null;
};

// Helper function to find phase containing a loop
const findPhaseForLoop = (project, loopId) => {
    for (const phase of project.phases || []) {
        if (phase.loops?.some(l => l.id === loopId)) {
            return phase;
        }
    }
    return null;
};

// Create Table of Contents section
const createTableOfContentsSection = () => {
    return [
        new Paragraph({
            text: "Inhoudsopgave",
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 },
        }),
        new TableOfContents("Summary", { hyperlink: true, headingStyle: "1-5" }),
    ];
};

// Create title section
const createTitleSection = (project) => {
    return [
        new Paragraph({
            text: project.name,
            heading: HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        }),
        new Paragraph({
            text: `Project Export`,
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
        }),
        new Paragraph({
            text: `Gegenereerd op: ${formatDate(new Date())}`,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        }),
        new Paragraph({
            text: "",
            pageBreakBefore: true,
        }),
    ];
};

/** Returns trimmed non-empty string, or "Geen" when null/empty/whitespace-only. */
const textOrGeen = (value) => {
    if (value == null) return 'Geen';
    const s = String(value);
    return s.trim() === '' ? 'Geen' : s;
};

const EXPLANATION_BOX_BORDER_SIDE = {
    style: BorderStyle.SINGLE,
    size: 6,
    space: 2,
    color: '1d9fd6',
};

/** Empty paragraph with a thin blue border for handwritten notes in the printed export. */
const createExplanationPlaceholderParagraph = () =>
    new Paragraph({
        children: [new TextRun({ text: 'Uitleg', bold: true })],
        border: {
            top: EXPLANATION_BOX_BORDER_SIDE,
            bottom: EXPLANATION_BOX_BORDER_SIDE,
            left: EXPLANATION_BOX_BORDER_SIDE,
            right: EXPLANATION_BOX_BORDER_SIDE,
        },
        shading: {
            color: '1d9fd6',
            intensity: 50,
            type: ShadingType.PERCENT_50,
        },
        spacing: {
            before: 80,
            after: 200,
            line: 360,
            lineRule: LineRuleType.EXACT,
        },
    });

// Create project summary section
const createProjectSummary = (project, includeExplanationTextBoxes = false) => {
    const children = [
        new Paragraph({
            text: "Projectsamenvatting",
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 },
            pageBreakBefore: true,
        }),
    ];
    
    // Project name and description
    if (project.name) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Naam: ", bold: true }),
                    new TextRun({ text: project.name }),
                ],
                spacing: { after: 200 },
            })
        );
    }
        
    // Dates and status
    if (project.start_date) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Startdatum: ", bold: true }),
                    new TextRun({ text: formatDate(project.start_date) }),
                ],
                spacing: { after: 200 },
            })
        );
    }
    
    if (project.status !== undefined) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Status: ", bold: true }),
                    new TextRun({ text: getStatusLabel(project.status) }),
                ],
                spacing: { after: 200 },
            })
        );
    }
    
    // Team members
    if (project.owners && project.owners.length > 0) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Eigenaren: ", bold: true }),
                    new TextRun({ text: formatUserList(project.owners) }),
                ],
                spacing: { after: 200 },
            })
        );
    }
    
    if (project.collaborators && project.collaborators.length > 0) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Bijdragers: ", bold: true }),
                    new TextRun({ text: formatUserList(project.collaborators) }),
                ],
                spacing: { after: 200 },
            })
        );
    }
    
    if (project.readers && project.readers.length > 0) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Lezers: ", bold: true }),
                    new TextRun({ text: formatUserList(project.readers) }),
                ],
                spacing: { after: 200 },
            })
        );
    }

    // Extra project information
    // Add divider before project description
    children.push(
        new Paragraph({
            text: "",
            spacing: { before: 200, after: 200 },
            border: {
                bottom: {
                    color: "auto",
                    space: 1,
                    style: BorderStyle.SINGLE,
                    size: 6,
                },
            },
        })
    );
    
    children.push(
        new Paragraph({
            children: [
                new TextRun({ text: "Beschrijving: ", bold: true }),
                new TextRun({ text: textOrGeen(project.description) }),
            ],
            spacing: { after: 200 },
        })
    );

    children.push(
        new Paragraph({
            children: [
                new TextRun({ text: "Beleid en wet- en regelgeving duurzaamheid: ", bold: true }),
                new TextRun({ text: textOrGeen(project.sectorSustainabilityPolicy) }),
            ],
            spacing: { after: 200 },
        })
    );

    children.push(
        new Paragraph({
            children: [
                new TextRun({ text: "Duurzaamheidsbeleid van relevante stakeholders: ", bold: true }),
                new TextRun({ text: textOrGeen(project.stakeholderSustainabilityPolicy) }),
            ],
            spacing: { after: 200 },
        })
    );

    children.push(
        new Paragraph({
            children: [
                new TextRun({ text: "Duurzaamheidsaanpak: ", bold: true }),
                new TextRun({ text: textOrGeen(project.sustainabilityApproach) }),
            ],
            spacing: { after: 200 },
        })
    );

    if (includeExplanationTextBoxes) {
        children.push(createExplanationPlaceholderParagraph());
    }
    
    // // Add page break
    // children.push(
    //     new Paragraph({
    //         text: "",
    //         pageBreakAfter: true,
    //     })
    // );
    
    return children;
};

// Create loop header
const createLoopHeader = (phase, loop) => {
    const phaseName = phase?.phase_name || `Fase ${phase?.phase}`;
    const loopName = loop?.name || `Ronde ${loop?.loop_number || ''}`;
    
    return [
        new Paragraph({
            text: `${phaseName} - ${loopName}`,
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 },
            pageBreakBefore: true,
        }),
      
    ];
};

// Create loop setup section
const createLoopSetup = (loop, flatKpis, flatThemes, loopSelection = {}, chartImages = [], includeExplanationTextBoxes = false) => {
    const children = [
        new Paragraph({
            text: "Setup",
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200 },
        }),
    ];
    
    // Selected themes
    if (loop.loop_themes && loop.loop_themes.length > 0) {
        children.push(
            new Paragraph({
                text: "Aspecten",
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 150 },
            })
        );

        const themes = loop.loop_themes.map((th) => ({...th, theme_details: flatThemes[th.theme]})).sort((a, b) => a.theme_details.ordering - b.theme_details.ordering)
        
        const themeRows = themes
            .map(loopTheme => {
                const theme = flatThemes[loopTheme.theme];
                if (!theme) return null;
                
                return new TableRow({
                    children: [
                        new TableCell({
                            children: [new Paragraph(loopTheme.theme_details.name || 'N/A')],
                            width: { size: 30, type: WidthType.PERCENTAGE },
                        }),
                        new TableCell({
                            children: [new Paragraph(loopTheme.description || '')],
                            width: { size: 50, type: WidthType.PERCENTAGE },
                        }),
                        new TableCell({
                            children: [new Paragraph(importanceLevels[loopTheme.importance]?.label || 'Niet ingevuld')],
                            width: { size: 20, type: WidthType.PERCENTAGE },
                        }),
                    ],
                });
            })
            .filter(row => row !== null);
        
        if (themeRows.length > 0) {
            children.push(
                new Table({
                    rows: [
                        new TableRow({
                            children: [
                                new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: "Thema", bold: true })] })],
                                    width: { size: 30, type: WidthType.PERCENTAGE },
                                }),
                                new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: "Beschrijving", bold: true })] })],
                                    width: { size: 50, type: WidthType.PERCENTAGE },
                                }),
                                new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: "Niveau", bold: true })] })],
                                    width: { size: 20, type: WidthType.PERCENTAGE },
                                }),
                            ],
                        }),
                        ...themeRows,
                    ],
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    keepLines: true,
                })
            );
        }
        
        // Add theme polar chart if selected
        if (loopSelection.includeThemeGraph && chartImages.length > 0) {
            const themeChartImage = chartImages.find(
                img => img.loopId === loop.id && img.kpiId === 'theme-polar'
            );
            
            if (themeChartImage?.imageData) {
                try {
                    const imageBuffer = base64ToBuffer(themeChartImage.imageData);
                    children.push(
                        new Paragraph({
                            children: [
                                new ImageRun({
                                    type: 'jpg',
                                    data: imageBuffer,
                                    transformation: {
                                        width: 600,
                                        height: 300,
                                    },
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 300 },
                        })
                    );
                } catch (error) {
                    console.error('Error adding theme polar chart:', error);
                    children.push(
                        new Paragraph({
                            text: "Fout bij het toevoegen van thema's grafiek",
                            italics: true,
                            spacing: { after: 200 },
                        })
                    );
                }
            }
        }
    }
    
    // Selected KPIs with goals
    if (loop.loop_kpis && loop.loop_kpis.length > 0) {
        children.push(
            new Paragraph({
                text: "KPI's en Doelen",
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 150, before: 200 },
            })
        );
        
        const kpiRows = loop.loop_kpis
            .filter(lk => lk.is_selected)
            .map(loopKpi => {
                const kpi = flatKpis[loopKpi.kpi];
                const goal = loopKpi.loop_goals?.[0];
                
                return new TableRow({
                    children: [
                        new TableCell({
                            children: [new Paragraph(kpi?.name || 'N/A')],
                            width: { size: 20, type: WidthType.PERCENTAGE },
                        }),
                        new TableCell({
                            children: [new Paragraph(kpi?.description || '')],
                            width: { size: 30, type: WidthType.PERCENTAGE },
                        }),
                        new TableCell({
                            children: [new Paragraph(goal ? formatScore(goal, kpi) : 'Geen doel')],
                            width: { size: 20, type: WidthType.PERCENTAGE },
                        }),
                        new TableCell({
                            children: [new Paragraph(goal ? goal.description : 'Geen doel')],
                            width: { size: 30, type: WidthType.PERCENTAGE },
                        }),
                    ],
                });
            });
        
        if (kpiRows.length > 0) {
            children.push(
                new Table({
                    rows: [
                        new TableRow({
                            children: [
                                new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: "KPI", bold: true })] })],
                                    width: { size: 20, type: WidthType.PERCENTAGE },
                                }),
                                new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: "Beschrijving", bold: true })] })],
                                    width: { size: 30, type: WidthType.PERCENTAGE },
                                }),
                                new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: "Doel", bold: true })] })],
                                    width: { size: 20, type: WidthType.PERCENTAGE },
                                }),
                                new TableCell({
                                    children: [new Paragraph({ children: [new TextRun({ text: "Uitleg", bold: true })] })],
                                    width: { size: 30, type: WidthType.PERCENTAGE },
                                }),
                            ],
                        }),
                        ...kpiRows,
                    ],
                    width: { size: 100, type: WidthType.PERCENTAGE },
                })
            );
        }
    }
    
    // Layers
    if (loop.loop_layers && loop.loop_layers.length > 0) {
        children.push(
            new Paragraph({
                text: "Onderdelen",
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 150, before: 200 },
            })
        );
        children.push(
            new Paragraph({
                text: "De volgende onderdelen zijn geselecteerd voor deze ronde:",
                spacing: { after: 100 },
            })
        );
        
        loop.loop_layers.forEach(loopLayer => {
            children.push(
                new Paragraph({
                    text: `${loopLayer.layer_details?.name || 'Onbekend onderdeel'}`,
                    bullet: {
                        level: 0,
                    }
                })
            );
        });
    }
    
    // Segments
    if (loop.loop_segments && loop.loop_segments.length > 0) {
        children.push(
            new Paragraph({
                text: "Dijkvakken",
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 150, before: 200 },
            })
        );

        // Calculate total segment length
        const totalSegmentLength = loop.loop_segments.reduce((sum, segment) => {
            return sum + (segment.length || 0);
        }, 0);

        const segmentRows = loop.loop_segments.map(segment => {
            return new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph(segment.name || 'N/A')],
                        width: { size: 30, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [new Paragraph(`${formatValue(segment.length || 0, "m")}`)],
                        width: { size: 20, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [new Paragraph(segment.description || '')],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                    }),
                ],
            });
        });

        children.push(
            new Table({
                rows: [
                    // Header row
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({ children: [new TextRun({ text: "Dijkvak", bold: true })] })],
                                width: { size: 30, type: WidthType.PERCENTAGE },
                            }),
                            new TableCell({
                                children: [new Paragraph({ children: [new TextRun({ text: "Lengte", bold: true })] })],
                                width: { size: 20, type: WidthType.PERCENTAGE },
                            }),
                            new TableCell({
                                children: [new Paragraph({ children: [new TextRun({ text: "Beschrijving", bold: true })] })],
                                width: { size: 50, type: WidthType.PERCENTAGE },
                            }),
                        ],
                    }),
                    // Data rows
                    ...segmentRows,
                    // Footer row with total
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({ text: "Totaal", bold: true })],
                                width: { size: 30, type: WidthType.PERCENTAGE },
                            }),
                            new TableCell({
                                children: [new Paragraph({ text: `${formatValue(totalSegmentLength, "m")}`, bold: true })],
                                width: { size: 20, type: WidthType.PERCENTAGE },
                            }),
                            new TableCell({
                                children: [new Paragraph("")],
                                width: { size: 50, type: WidthType.PERCENTAGE },
                            }),
                        ],
                    }),
                ],
                width: { size: 100, type: WidthType.PERCENTAGE },
            })
        );

        if (includeExplanationTextBoxes) {
            children.push(createExplanationPlaceholderParagraph());
        }
    }
    
    // Reference design
    if (loop.reference_design) {
        children.push(
            new Paragraph({
                text: "Referentieontwerp",
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 150, before: 200 },
            })
        );
        
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Naam: ", bold: true }),
                    new TextRun({ text: loop.reference_design.name }),
                ],
                spacing: { after: 100 },
            })
        );
        
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: "Beschrijving: ", bold: true }),
                    new TextRun({ text: loop.reference_design.description || "Geen beschrijving" }),
                ],
                spacing: { after: 200 },
                italics: true,
            })
        );
    }

    // Designs
    if (loop.designs && loop.designs.length > 0) {
        children.push(
            new Paragraph({
                text: "Ontwerpen",
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 150, before: 200 },
            })
        );

        loop.designs.forEach(design => {
            children.push(
                new Paragraph({
                    children: [
                        new TextRun({ text: "Naam: ", bold: true }),
                        new TextRun({ text: design.name }),
                    ],
                    spacing: { after: 100 },
                })
            );
            children.push(
                new Paragraph({
                    children: [
                        new TextRun({ text: "Beschrijving: ", bold: true }),
                        new TextRun({ text: design.description || "Geen beschrijving" }),
                    ],
                    spacing: { after: 200 },
                    italics: true,
                })
            );
        });
    }
    
    return children;
};

// Create loop advice section
const createLoopAdvice = (loop, flatThemes) => {
    const children = [
        new Paragraph({
            text: "Adviseren",
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200, before: 300 },
        }),
    ];

    if (loop.advice_by_themes && Object.keys(loop.advice_by_themes).length > 0) {
        // Collect advice rows
        const adviceRows = [];
        
        loop.loop_themes.forEach(loopTheme => {
            const theme = flatThemes[loopTheme.theme];
            const advice = loop.advice_by_themes[loopTheme.id];
            
            if (advice && advice.text) {
                adviceRows.push(
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({ text: theme?.name || 'Geen naam', style: 'bold' })],
                                width: { size: 25, type: WidthType.PERCENTAGE },
                                verticalAlign: VerticalAlign.TOP,
                            }),
                            new TableCell({
                                children: [new Paragraph(advice.text || 'Geen advies')],
                                width: { size: 75, type: WidthType.PERCENTAGE },
                                verticalAlign: VerticalAlign.TOP,
                            }),
                        ],
                    })
                );
            }
        });
        
        if (adviceRows.length > 0) {
            children.push(
                new Table({
                    rows: [
                        new TableRow({
                            children: [
                                new TableCell({
                                    children: [new Paragraph({children: [new TextRun({ text: "Aspect", bold: true})]})],
                                    width: { size: 25, type: WidthType.PERCENTAGE },
                                }),
                                new TableCell({
                                    children: [new Paragraph({children: [new TextRun({ text: "Advies", bold: true })]})],
                                    width: { size: 75, type: WidthType.PERCENTAGE },
                                }),
                            ],
                        }),
                        ...adviceRows,
                    ],
                    width: { size: 100, type: WidthType.PERCENTAGE },
                })
            );
        } else {
            children.push(
                new Paragraph({
                    text: "Geen adviezen beschikbaar",
                    spacing: { after: 200 },
                })
            );
        }
    } else {
        children.push(
            new Paragraph({
                text: "Geen adviezen beschikbaar",
                spacing: { after: 200 },
            })
        );
    }
    
    return children;
};

// Create scores table
const createScoresTable = (loop, loopSelection, flatKpis) => {
    const children = [
        new Paragraph({
            text: "Toetsen",
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200, before: 300 },
        }),
    ];
    
    // Get selected designs
    const selectedDesigns = (loop.designs || [])
        .filter(d => loopSelection.designs.includes(d.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    
    if (selectedDesigns.length === 0) {
        children.push(
            new Paragraph({
                text: "Geen ontwerpen geselecteerd",
                spacing: { after: 200 },
            })
        );
        return children;
    }
    
    // Add reference design if it exists
    let designs = selectedDesigns;
    if (loop.reference_design) {
        designs = [loop.reference_design, ...selectedDesigns];
    }
    
    // Get selected KPIs
    const selectedKpis = (loop.loop_kpis || [])
        .filter(lk => lk.is_selected)
        .sort((a, b) => {
            const kpiA = flatKpis[a.kpi];
            const kpiB = flatKpis[b.kpi];
            return kpiA?.name.localeCompare(kpiB?.name);
        });
    
    // Create header row
    const headerCells = [
        new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "KPI", bold: true })] })],
            verticalAlign: VerticalAlign.CENTER,
        }),
    ];
    
    if (loop.reference_design) {
        headerCells.push(
            new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: "Doel", bold: true })] })],
                verticalAlign: VerticalAlign.CENTER,
            })
        );
    }
    
    designs.forEach(design => {
        headerCells.push(
            new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: design.name, bold: true })] })],
                verticalAlign: VerticalAlign.CENTER,
            })
        );
    });
    
    const tableRows = [new TableRow({ children: headerCells })];
    
    // Cost row
    const costCells = [
        new TableCell({
            children: [new Paragraph("Kosten")],
        }),
    ];
    
    if (loop.reference_design) {
        costCells.push(new TableCell({ children: [new Paragraph("")] }));
    }
    
    designs.forEach(design => {
        costCells.push(
            new TableCell({
                children: [new Paragraph(formatValue(design.investment_cost, "€"))],
            })
        );
    });
    
    tableRows.push(new TableRow({ children: costCells }));
    
    // KPI rows
    selectedKpis.forEach(loopKpi => {
        const kpi = flatKpis[loopKpi.kpi];
        if (!kpi) return;
        
        const goal = loopKpi.loop_goals?.[0];
        
        const kpiCells = [
            new TableCell({
                children: [
                    new Paragraph(kpi.name),
                    ...(kpi.unit ? [new Paragraph({ text: `(${kpi.unit})`, italics: true })] : []),
                ],
            }),
        ];
        
        if (loop.reference_design) {
            kpiCells.push(
                new TableCell({
                    children: [new Paragraph(goal ? formatScore(goal, kpi) : "N/A")],
                })
            );
        }
        
        designs.forEach(design => {
            const score = design.scores?.find(s => s.kpi === kpi.id);
            kpiCells.push(
                new TableCell({
                    children: [new Paragraph(score ? formatScore(score, kpi) : "Geen score")],
                })
            );
        });
        
        tableRows.push(new TableRow({ children: kpiCells }));
    });
    
    children.push(
        new Table({
            rows: tableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
        })
    );
    
    return children;
};

// Create graphs section
const createGraphsSection = (loopSelection, chartImages, flatKpis, includeExplanationTextBoxes = false) => {
    const children = [
        new Paragraph({
            text: "Grafieken",
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200, before: 300 },
        }),
    ];
    
    loopSelection.graphs.forEach((graphSelection, index) => {
        const chartImage = chartImages.find(
            img => img.loopId === loopSelection.loopId && img.kpiId === graphSelection.kpiId
        );
        
        if (!chartImage || !chartImage.imageData) return;
        
        const kpi = graphSelection.kpiId === 'cost' 
            ? { name: 'Kosten', unit: '€' }
            : flatKpis[graphSelection.kpiId];
        
        if (!kpi) return;
        
        // Graph title
        children.push(
            new Paragraph({
                text: `${kpi.name}${kpi.unit ? ` (${kpi.unit})` : ''}`,
                heading: HeadingLevel.HEADING_3,
                spacing: { after: 150, before: index > 0 ? 200 : 0 },
                keepNext: true,
            })
        );
        
        // Add image
        try {
            const imageBuffer = base64ToBuffer(chartImage.imageData);
            children.push(
                new Paragraph({
                    children: [
                        new ImageRun({
                            type: 'jpg',
                            data: imageBuffer,
                            transformation: {
                                width: 600,
                                height: 300,
                            },
                        }),
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: { after: includeExplanationTextBoxes ? 120 : 300 },
                })
            );
            if (includeExplanationTextBoxes) {
                children.push(createExplanationPlaceholderParagraph());
            }
        } catch (error) {
            console.error('Error adding chart image:', error);
            children.push(
                new Paragraph({
                    text: "Fout bij het toevoegen van grafiek",
                    italics: true,
                    spacing: { after: includeExplanationTextBoxes ? 120 : 200 },
                })
            );
            if (includeExplanationTextBoxes) {
                children.push(createExplanationPlaceholderParagraph());
            }
        }
    });
    
    return children;
};

// Create conclusions section
const createConclusionsSection = (loop) => {
    const children = [
        new Paragraph({
            text: "Concluderen",
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200, before: 300 },
        }),
        new Paragraph({
            text: "Kansrijke alternatieven",
            heading: HeadingLevel.HEADING_3,
            spacing: { after: 150 },
        })
    ];

    const headerCells = [
        new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Ontwerp", bold: true })] })],
            verticalAlign: VerticalAlign.CENTER,
        }),
        new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Meegenomen als KRA", bold: true })] })],
            verticalAlign: VerticalAlign.CENTER,
        }),
        new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Uitleg", bold: true })] })],
            verticalAlign: VerticalAlign.CENTER,
        }),
    ];

    const tableRows = [new TableRow({ children: headerCells })];

    loop.designs.forEach(design => {
        tableRows.push(new TableRow({ children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: design.name })] })], }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: design.is_preferred ? "Ja" : "Nee" })] })], }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: design.comment || "" })] })], }),
        ] }));
    });

    if (loop.designs.length > 0) {
        children.push(
            new Table({
                rows: tableRows,
                width: { size: 100, type: WidthType.PERCENTAGE },
            })
        );
    } else {
        children.push(
            new Paragraph({
                text: "Geen ontwerpen beschikbaar",
                spacing: { after: 200 },
            })
        );
    }

    // Conclusies
    children.push(
        new Paragraph({
            text: "Conclusies",
            heading: HeadingLevel.HEADING_3,
            spacing: { after: 150, before: 150 },
        }),
        new Paragraph({ children: [new TextRun({ text: loop.comment || "Geen conclusies toegevoegd" })] }),
    );

    // Aanbevelingen
    children.push(
        new Paragraph({
            text: "Aanbevelingen",
            heading: HeadingLevel.HEADING_3,
            spacing: { after: 150, before: 150 },
        }),
    );
    if (loop.recommendations.length > 0) {
        loop.recommendations.forEach(recommendation => {
            children.push(new Paragraph({ children: [new TextRun({ text: recommendation })] }));
        });
    } else {
        children.push(new Paragraph({ text: "Geen aanbevelingen toegevoegd", spacing: { after: 200 } }));
    }

    // Besluiten
    children.push(
        new Paragraph({
            text: "Besluiten",
            heading: HeadingLevel.HEADING_3,
            spacing: { after: 150, before: 150 },
        }),
    );
    if (loop.decisions.length > 0) {
        loop.decisions.forEach(decision => {
            children.push(new Paragraph({ children: [new TextRun({ text: decision })] }));
        });
    } else {
        children.push(new Paragraph({ text: "Geen besluiten toegevoegd", spacing: { after: 200 } }));
    }
    return children;
};

